/**
 * Client-side JPEG downscaler for uploaded photos.
 *
 * Teachers capture at whatever the camera gives us (often 1080p–4K on modern
 * phones). Full-size uploads would burn Supabase Storage egress fast, so we
 * downscale to a reasonable max edge before upload.
 *
 * Default: 1600px longest edge, JPEG quality 0.85 → typically ~150–250 KB per
 * photo, still plenty of detail for a classroom documentation report.
 */

const DEFAULT_MAX_EDGE = 1600;
const DEFAULT_QUALITY = 0.85;

export interface ResizeOptions {
  maxEdge?: number;
  quality?: number;
}

async function drawToCanvas(
  source: HTMLVideoElement | HTMLImageElement | HTMLCanvasElement,
  naturalWidth: number,
  naturalHeight: number,
  maxEdge: number,
): Promise<HTMLCanvasElement> {
  const scale = Math.min(1, maxEdge / Math.max(naturalWidth, naturalHeight));
  const width = Math.round(naturalWidth * scale);
  const height = Math.round(naturalHeight * scale);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not acquire 2D canvas context');
  ctx.drawImage(source, 0, 0, width, height);
  return canvas;
}

function canvasToBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) reject(new Error('Canvas toBlob returned null'));
        else resolve(blob);
      },
      'image/jpeg',
      quality,
    );
  });
}

/**
 * Capture a frame from a live <video> element, downscale, return a JPEG Blob.
 * Used from the phase 6 camera preview.
 */
export async function captureVideoFrameAsJpeg(
  video: HTMLVideoElement,
  opts: ResizeOptions = {},
): Promise<Blob> {
  const maxEdge = opts.maxEdge ?? DEFAULT_MAX_EDGE;
  const quality = opts.quality ?? DEFAULT_QUALITY;
  if (!video.videoWidth || !video.videoHeight) {
    throw new Error('Video has no dimensions yet — wait for loadedmetadata');
  }
  const canvas = await drawToCanvas(video, video.videoWidth, video.videoHeight, maxEdge);
  return canvasToBlob(canvas, quality);
}

/**
 * Downscale an arbitrary image Blob/File (gallery import, paste, etc.) to JPEG.
 */
export async function resizeImageBlob(
  input: Blob,
  opts: ResizeOptions = {},
): Promise<Blob> {
  const maxEdge = opts.maxEdge ?? DEFAULT_MAX_EDGE;
  const quality = opts.quality ?? DEFAULT_QUALITY;
  const url = URL.createObjectURL(input);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('Could not decode image'));
      el.src = url;
    });
    const canvas = await drawToCanvas(img, img.naturalWidth, img.naturalHeight, maxEdge);
    return canvasToBlob(canvas, quality);
  } finally {
    URL.revokeObjectURL(url);
  }
}
